import { useEffect, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { StatusCard } from '@/components/ui/card/StatusCard';
import { useTranslation } from '@/hooks/useTranslation';
import type { AdminApiClient } from '@/features/admin/api/AdminApiClient';

export type CompanyFormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'rename';
  company?: string;
  client: AdminApiClient;
  onSuccess: () => void;
};

export function CompanyFormDialog({ open, onOpenChange, mode, company, client, onSuccess }: CompanyFormDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSubmitting(false);
    setName(mode === 'rename' ? (company ?? '') : '');
  }, [open, mode, company]);

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    const res =
      mode === 'create' ? await client.createCompany(name) : await client.renameCompany(company!, name);
    setSubmitting(false);
    if (res.ok) {
      onSuccess();
      onOpenChange(false);
    } else {
      setError(res.message ?? t('administration.errors.generic'));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? t('administration.company.createTitle') : t('administration.company.renameTitle')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <Label htmlFor="company-name">{t('administration.company.name')}</Label>
          <Input id="company-name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        {error && <StatusCard type="error" message={error} />}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleSubmit}
            disabled={name.trim().length === 0}
            loading={submitting}
          >
            {mode === 'create' ? t('administration.company.create') : t('administration.company.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
